import { Children, isValidElement, type ReactNode } from 'react';

import { Combobox, type ComboboxOption } from './Combobox';

interface SelectProps {
  id?: string;
  'aria-label'?: string;
  value: string;
  /**
   * Dieselbe Form wie am ``<select>``: ``event.target.value``. So bleibt
   * jeder Aufrufer, wie er war -- umgestellt wird das Element, nicht die
   * Logik dahinter.
   */
  onChange: (event: { target: { value: string } }) => void;
  disabled?: boolean;
  /** Wird ignoriert; das Feld bringt seine Gestaltung mit. */
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
  /** ``<option>``-Elemente, wie im ``<select>``. */
  children: ReactNode;
}

/** Der Text eines ``<option>`` -- auch wenn er aus mehreren Teilen besteht. */
function textOf(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement<{ children?: ReactNode }>(node)) return textOf(node.props.children);
  return '';
}

/** Sammelt die ``<option>``-Elemente ein, auch aus Fragmenten und Listen. */
function collect(children: ReactNode, into: ComboboxOption[]): ComboboxOption[] {
  Children.forEach(children, (child) => {
    if (!isValidElement<{ value?: unknown; children?: ReactNode }>(child)) return;
    if (child.type === 'option') {
      into.push({
        id:
          typeof child.props.value === 'string' ? child.props.value : textOf(child.props.children),
        label: textOf(child.props.children),
      });
      return;
    }
    collect(child.props.children, into);
  });
  return into;
}

/**
 * Eine Aufklappliste, in die man tippen kann -- Etappe 2 der
 * Autovervollstaendigung (F1-49).
 *
 * **Im Code ein ``<select>``, auf dem Bildschirm ein Suchfeld.** Die
 * Schnittstelle ist bewusst die des nativen Elements: ``value``,
 * ``onChange`` mit ``event.target.value``, ``<option>``-Kinder. Mehr als
 * sechzig Auswahllisten liessen sich so umstellen, ohne an jeder Stelle die
 * Logik anzufassen -- und wer eine neue schreibt, schreibt sie wie gewohnt.
 *
 * **Die leere Wahl bleibt waehlbar.** Eine Option mit ``value=""`` ("Ohne
 * Kategorie", "Nicht zugewiesen") steht in der Liste und im leeren Feld als
 * Platzhalter; das Kreuz im Feld fuehrt zu ihr zurueck. Gibt es keine, ist
 * die Auswahl Pflicht und das Kreuz fehlt.
 *
 * Gefiltert wird im Browser: Die Listen, die hier stehen, sind geladen.
 * Kunden, Tickets und Adressen suchen eigene Felder beim Server
 * (``CustomerCombobox``, ``TicketCombobox``, ``EmailCombobox``).
 */
export function Select({
  id,
  'aria-label': ariaLabel,
  value,
  onChange,
  disabled,
  style,
  placeholder,
  children,
}: SelectProps) {
  const options = collect(children, []);
  const empty = options.find((option) => option.id === '');
  const chosen = value === '' ? null : (options.find((option) => option.id === value) ?? null);

  return (
    <Combobox
      id={id}
      ariaLabel={ariaLabel}
      value={chosen}
      options={options}
      disabled={disabled}
      style={style}
      clearable={empty !== undefined}
      placeholder={placeholder ?? empty?.label}
      onChange={(option) => {
        const next = option?.id ?? '';
        if (next !== value) onChange({ target: { value: next } });
      }}
    />
  );
}
